import React, { useRef } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

import { RadiantSpinner } from 'components/RadiantSpinner'

import { purchaseUpgradeAction } from 'root-redux/actions/common'
import { selectUUID, selectUserSubscriptionInfo } from 'root-redux/selects/user'
import { TAppDispatch } from 'root-redux/store/store'

import { getBillingPeriodTranslation } from 'modules/unsubscribe/helpers'
import { OPTION_VALUES } from 'modules/unsubscribe/pages/DiscountOffer/constants'
import { useDiscountOffer } from 'modules/unsubscribe/pages/DiscountOffer/useDiscountOffer'

import { eventLogger } from 'services/eventLogger.service'

import { goTo } from 'browser-history'
import { PageId } from 'page-constants'
import { CENTS_IN_DOLLAR, CURRENCY_SYMBOLS } from 'root-constants'

import { FlowButton } from '../../components/FlowButton'
import { FlowFeedbackPopup } from '../../components/FlowFeedbackPopup'
import { FlowPageContainer } from '../../components/FlowPageContainer'
import {
  formatAmount,
  getDailyPrice,
  roundToNinetyNineCents,
} from '../../helpers'
import { useCancelSubscription } from '../../hooks/useCancelSubscription'
import { useRetentionFlowV2Config } from '../../hooks/useRetentionFlowV2Config'
import { FlowBottomArea, FlowContent } from '../../styles'
import { StyledFlowDiscountOffer as S } from './FlowDiscountOffer.styles'

export const FlowDiscountOffer: React.FC = () => {
  const { search } = useLocation()
  const { t } = useTranslation()
  const { copyKey, hasPlanPriceCurrency } = useRetentionFlowV2Config()
  const dispatch: TAppDispatch = useDispatch()
  const userSubscriptionInfo = useSelector(selectUserSubscriptionInfo)
  const userId = useSelector(selectUUID)
  const { subscription: offer, discountPercentage } = useDiscountOffer()

  const {
    isFeedbackPopupShown,
    closeFeedbackPopup,
    cancelSubscription,
    goToCancellation,
  } = useCancelSubscription()
  const isSubmittedRef = useRef(false)

  const handleKeepPlan = () => {
    if (isSubmittedRef.current) return

    isSubmittedRef.current = true
    eventLogger.logDiscountOfferScreenClose(OPTION_VALUES.YES, userId)
    dispatch(purchaseUpgradeAction())
    goTo(
      { pathname: PageId.CONTINUED_SUBSCRIPTION, search },
      { isUpgrade: true },
    )
  }

  const handleCancelPlan = () => {
    eventLogger.logDiscountOfferScreenClose(OPTION_VALUES.NO, userId)
    goToCancellation()
  }

  if (!offer || !userSubscriptionInfo) {
    return (
      <FlowPageContainer hasNavigation>
        <RadiantSpinner />
      </FlowPageContainer>
    )
  }

  const { periodName, periodQuantity } = offer.mainPrices
  const offerCurrencySymbol = CURRENCY_SYMBOLS[offer.currency]
  const currentPlanPriceSymbol = hasPlanPriceCurrency
    ? userSubscriptionInfo.currencySymbol
    : ''
  const offerPlanPriceSymbol = hasPlanPriceCurrency ? offerCurrencySymbol : ''
  const currentPrice =
    userSubscriptionInfo.subAmount || userSubscriptionInfo.price
  const currentDailyPrice = getDailyPrice(
    currentPrice,
    userSubscriptionInfo.interval,
    userSubscriptionInfo.intervalCount,
  )
  const offerPrice = offer.mainPrices.fullPrice
  const offerDailyPrice = getDailyPrice(offerPrice, periodName, periodQuantity)
  const offerOldPrice = offer.mainPrices.oldPrices.fullPrice
  const offerDisplayedOldPrice = roundToNinetyNineCents(offerOldPrice)
  const offerOldDailyPrice = getDailyPrice(
    offerOldPrice,
    periodName,
    periodQuantity,
  )
  const offerBillingPeriod = getBillingPeriodTranslation(
    periodName,
    periodQuantity,
  )
  const amountToPay = (offer.upgradeDiff?.amountToPay || 0) / CENTS_IN_DOLLAR

  return (
    <FlowPageContainer hasNavigation>
      <S.Content>
        <FlowContent>
          <S.Title>
            <Trans
              i18nKey={`${copyKey}.discountOffer.title`}
              values={{ percentage: discountPercentage }}
              components={{ br: <br />, mark: <mark /> }}
            />
          </S.Title>
          <S.Description>
            {t(`${copyKey}.discountOffer.description`, {
              percentage: discountPercentage,
            })}
          </S.Description>

          <S.Plans>
            <div>
              <S.Tag>{t(`${copyKey}.discountOffer.currentPlan`)}</S.Tag>
              <S.CurrentPlanBody>
                <S.PlanInfo>
                  <S.PlanName>
                    {t(`${copyKey}.discountOffer.planName`, {
                      context: userSubscriptionInfo.interval,
                      count: userSubscriptionInfo.intervalCount,
                    })}
                  </S.PlanName>
                  <S.PlanPrice>
                    {currentPlanPriceSymbol}
                    {formatAmount(currentPrice, userSubscriptionInfo.currency)}
                  </S.PlanPrice>
                </S.PlanInfo>
                <S.Divider />
                <S.PricePerDay>
                  <S.DailyPrice>
                    {userSubscriptionInfo.currencySymbol}
                    {formatAmount(
                      currentDailyPrice,
                      userSubscriptionInfo.currency,
                    )}
                  </S.DailyPrice>
                  <S.PerDay>{t(`${copyKey}.discountOffer.perDay`)}</S.PerDay>
                </S.PricePerDay>
              </S.CurrentPlanBody>
            </div>

            <div>
              <S.Tag isDark>
                {t(`${copyKey}.discountOffer.newPrice`, {
                  percentage: discountPercentage,
                })}
              </S.Tag>
              <S.NewPlanBody>
                <S.PlanRow>
                  <S.Check />
                  <S.PlanInfo>
                    <S.PlanName>
                      {t(`${copyKey}.discountOffer.planName`, {
                        context: periodName,
                        count: periodQuantity,
                      })}
                    </S.PlanName>
                    <S.PlanPrice>
                      <S.OldPrice>
                        {offerPlanPriceSymbol}
                        {formatAmount(offerDisplayedOldPrice, offer.currency)}
                      </S.OldPrice>
                      <span>→</span>
                      <span>
                        {offerPlanPriceSymbol}
                        {formatAmount(offerPrice, offer.currency)}
                      </span>
                    </S.PlanPrice>
                  </S.PlanInfo>
                  <S.Divider />
                  <S.PricePerDay>
                    <S.PlanPrice>
                      <S.OldPrice>
                        {offerCurrencySymbol}
                        {formatAmount(offerOldDailyPrice, offer.currency)}
                      </S.OldPrice>
                    </S.PlanPrice>
                    <S.DailyPrice>
                      {offerCurrencySymbol}
                      {formatAmount(offerDailyPrice, offer.currency)}
                    </S.DailyPrice>
                    <S.PerDay>{t(`${copyKey}.discountOffer.perDay`)}</S.PerDay>
                  </S.PricePerDay>
                </S.PlanRow>
                <S.Separator />
                <S.TodayRow>
                  <span>{t(`${copyKey}.discountOffer.youPayToday`)}</span>
                  <span>
                    {offerCurrencySymbol}
                    {amountToPay
                      ? formatAmount(amountToPay, offer.currency)
                      : 0}
                  </span>
                </S.TodayRow>
                <S.Disclaimer>
                  <Trans
                    i18nKey={`${copyKey}.discountOffer.disclaimer`}
                    values={{
                      price: `${offerCurrencySymbol}${formatAmount(
                        offerPrice,
                        offer.currency,
                      )}`,
                      period: t(offerBillingPeriod.key, {
                        count: offerBillingPeriod.count,
                      }),
                    }}
                  />
                </S.Disclaimer>
                <FlowButton type="button" onClick={handleKeepPlan}>
                  {t(`${copyKey}.discountOffer.keepPlan`)}
                </FlowButton>
                <S.DiscountStart>
                  {t(`${copyKey}.discountOffer.discountStart`)}
                </S.DiscountStart>
              </S.NewPlanBody>
            </div>
          </S.Plans>
        </FlowContent>
      </S.Content>
      <FlowBottomArea isFixed>
        <S.CancelButton
          type="button"
          variant="secondary"
          onClick={handleCancelPlan}
        >
          {t(`${copyKey}.discountOffer.cancelPlan`)}
        </S.CancelButton>
      </FlowBottomArea>
      <FlowFeedbackPopup
        isShown={isFeedbackPopupShown}
        onComplete={cancelSubscription}
        onClose={closeFeedbackPopup}
      />
    </FlowPageContainer>
  )
}
